import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Switch, Typography, Backdrop, CircularProgress } from '@mui/material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { useGestionEquipements } from './hooks/GestionEquipementsHook';

/**
 * Builds one-year-bucket age distribution data for chart display.
 * Each year from min to max age gets its own entry (bucket size = 1).
 *
 * @param {Array<Object>} data - Array of objects representing equipment with Age, Count_RealAge, Count_ApparentAge.
 * @returns {Array<Object>} Array of objects with label (year), Count_RealAge, Count_ApparentAge.
 */
export function formatAgeDistribution(data) {
  if (!data || data.length === 0) return [];
  const ages = data.map((row) => Number(row.Age));
  const minAge = Math.min(...ages);
  const maxAge = Math.max(...ages);

  // Validate that minAge and maxAge are valid numbers
  if (!isFinite(minAge) || !isFinite(maxAge)) {
    console.warn('formatAgeDistribution: Invalid age data detected (NaN or Infinity)');
    return [];
  }

  // Build a map for quick lookup
  const ageMap = {};
  data.forEach((row) => {
    const age = Number(row.Age);
    if (!ageMap[age]) {
      ageMap[age] = { Count_RealAge: 0, Count_ApparentAge: 0 };
    }
    ageMap[age].Count_RealAge += Number(row.Count_RealAge);
    ageMap[age].Count_ApparentAge += Number(row.Count_ApparentAge);
  });

  // Create one entry per year
  const result = [];
  for (let age = minAge; age <= maxAge; age++) {
    result.push({
      label: `${age}`,
      Age: age,
      Count_RealAge: ageMap[age]?.Count_RealAge ?? 0,
      Count_ApparentAge: ageMap[age]?.Count_ApparentAge ?? 0,
    });
  }
  return result;
}

/**
 * Returns the XAxis tick interval based on the number of data points so that
 * labels stay readable when there are many bars (up to 200+).
 *
 * @param {number} dataLength - The number of data points in the chart.
 * @returns {number} The interval between displayed ticks (0 means show all labels).
 */
function getTickInterval(dataLength) {
  if (dataLength > 120) return 9;
  if (dataLength > 80) return 4;
  if (dataLength > 40) return 1;
  return 0; // show every label
}

const GestionEquipements = () => {
  const { t } = useTranslation();
  const [showApparentAge, setShowApparentAge] = useState(false);
  const { ageDistribution, selectedFamily, isLoading } = useGestionEquipements();

  const chartData = useMemo(() => {
    if (!ageDistribution || ageDistribution.length === 0) return [];
    return formatAgeDistribution(ageDistribution);
  }, [ageDistribution]);

  const tickInterval = useMemo(() => getTickInterval(chartData.length), [chartData.length]);

  if (!selectedFamily)
    return (
      <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
        {t('commoncomponents.agingLaws.messages.selectEquipmentFamily', 'Please select an equipment family')}
      </Typography>
    );

  if (isLoading)
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  if (chartData.length === 0)
    return (
      <Typography variant="h6" align="center" style={{ marginTop: '20px' }}>
        Aucune donnée disponible pour la famille sélectionnée.
      </Typography>
    );

  return (
    <div>
      <Grid container spacing={2} alignItems="center">
        {/* Switch pour changer entre Age Réel et Age Apparent */}
        <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
          <span>{t('commoncomponents.agingLaws.chart.realAge', 'Real Age')}</span>
          <Switch
            checked={showApparentAge}
            onChange={(e) => setShowApparentAge(e.target.checked)}
            color="primary"
            inputProps={{ 'aria-label': 'switch between real and apparent age distribution' }}
          />
          <span>{t('commoncomponents.agingLaws.chart.apparentAge', 'Apparent Age')}</span>
        </Grid>
      </Grid>
      <ResponsiveContainer width="100%" height={350} style={{ marginTop: '10px', paddingBottom: '10px' }}>
        <BarChart data={chartData} barCategoryGap="1px" barGap={0} margin={{ top: 5, right: 20, left: 20, bottom: 25 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            type="category"
            tick={{ fontSize: chartData.length > 80 ? 9 : chartData.length > 40 ? 10 : 12, fill: '#888', dy: 6 }}
            tickLine={false}
            axisLine={{ stroke: '#ccc' }}
            interval={tickInterval}
            angle={chartData.length > 40 ? -45 : 0}
            textAnchor={chartData.length > 40 ? 'end' : 'middle'}
            allowDecimals={false}
          />
          <YAxis
            label={{
              value: t('commoncomponents.agingLaws.chart.numberOfEquipment', 'Number of equipment'),
              angle: -90,
              position: 'insideLeft',
              offset: -15,
              style: {
                textAnchor: 'middle',
              },
            }}
            mirror={true}
            allowDecimals={false}
          />
          <Tooltip
            formatter={(value, name) => {
              const label = t('commoncomponents.agingLaws.chart.numberOfEquipment', 'Number of equipment');
              return [value, label];
            }}
            labelFormatter={(label) => `${t('commoncomponents.agingLaws.chart.ageRange', 'Age range')}: ${label}`}
            cursor={true}
            contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
            itemStyle={{ color: '#000' }}
            labelStyle={{ color: '#000', fontWeight: 'bold' }}
          />
          <Legend iconSize={0} />
          <Bar
            dataKey={showApparentAge ? 'Count_ApparentAge' : 'Count_RealAge'}
            fill="#8884d8"
            name={
              showApparentAge
                ? t('commoncomponents.agingLaws.chart.apparentAge', 'Apparent Age')
                : t('commoncomponents.agingLaws.chart.realAge', 'Real Age')
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default GestionEquipements;
